import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Save, X, AlertCircle, CheckCircle, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { fairManagerApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';
import type { ContactSettingsResponse, ContactSettingsUpdate } from '../types';

/**
 * Fair Manager Contact Settings Component
 * Issue #1076 - Phase 3 of Issue #966: Contact Settings Management
 *
 * Allows Fair Managers to control visibility of their contact information
 * for a specific fair, providing per-fair contact customization and privacy control.
 */
export default function FairManagerContactSettings() {
  const { fairId } = useParams<{ fairId: string }>();
  const navigate = useNavigate();

  // State
  const [settings, setSettings] = useState<ContactSettingsUpdate>({
    show_contact_info: false,
    public_email: '',
    public_phone: ''
  });
  const [originalSettings, setOriginalSettings] = useState<ContactSettingsUpdate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Ref to store success timeout ID for cleanup
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load settings on mount
  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fairId]);

  // Track changes
  useEffect(() => {
    if (originalSettings) {
      const changed =
        settings.show_contact_info !== originalSettings.show_contact_info ||
        settings.public_email !== originalSettings.public_email ||
        settings.public_phone !== originalSettings.public_phone;
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  // Cleanup success timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const fetchSettings = async () => {
    if (!fairId) return;

    try {
      setLoading(true);
      setError(null);
      const response: ContactSettingsResponse = await fairManagerApi.getContactSettings(parseInt(fairId));

      const settingsData: ContactSettingsUpdate = {
        show_contact_info: response.show_contact_info,
        public_email: response.public_email || '',
        public_phone: response.public_phone || ''
      };

      setSettings(settingsData);
      setOriginalSettings(settingsData);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fairId) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Prepare data for API - always send email/phone to preserve values (Issue #1076)
      // The visibility flag controls display only, not storage
      const updateData: ContactSettingsUpdate = {
        show_contact_info: settings.show_contact_info,
        public_email: settings.public_email || undefined,
        public_phone: settings.public_phone || undefined
      };

      const response = await fairManagerApi.updateContactSettings(parseInt(fairId), updateData);

      // Update state from server response to ensure client-server sync (Issue #1076)
      const updatedSettings: ContactSettingsUpdate = {
        show_contact_info: response.show_contact_info,
        public_email: response.public_email || '',
        public_phone: response.public_phone || ''
      };

      setSuccess(true);
      setSettings(updatedSettings);
      setOriginalSettings(updatedSettings);
      setHasChanges(false);

      // Clear any existing timeout
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }

      // Clear success message after 3 seconds
      successTimeoutRef.current = setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalSettings) {
      setSettings(originalSettings);
      setHasChanges(false);
    }
    setError(null);
    setSuccess(false);
  };

  const handleBack = () => {
    navigate('/fairmanager/fairs');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading contact settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Fairs
          </button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="h-8 w-8 text-secondary" />
            Contact Settings
          </h1>
          <p className="mt-2 text-gray-600">
            Control how your contact information appears to vendors for this fair.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start"
            data-testid="error-message"
          >
            <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div
            className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start"
            data-testid="success-message"
          >
            <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-green-800">Success</h3>
              <p className="mt-1 text-sm text-green-700">Contact settings updated successfully.</p>
            </div>
          </div>
        )}

        {/* Settings Form */}
        <form onSubmit={handleSave} className="bg-white shadow rounded-lg">
          <div className="px-6 py-6 space-y-6">
            {/* Visibility Toggle */}
            <div>
              <label className="flex items-center cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={settings.show_contact_info}
                    onChange={(e) => setSettings({ ...settings, show_contact_info: e.target.checked })}
                    className="sr-only peer"
                    data-testid="show-contact-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </div>
                <span className="ml-3 flex items-center gap-2">
                  {settings.show_contact_info ? (
                    <Eye className="h-5 w-5 text-secondary" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  )}
                  <span className="text-sm font-medium text-gray-900">
                    Show contact information to vendors
                  </span>
                </span>
              </label>
              <p className="mt-2 text-sm text-gray-500">
                {settings.show_contact_info
                  ? 'Your contact information will be visible to vendors viewing this fair.'
                  : 'Your contact information will be hidden from vendors for this fair.'}
              </p>
            </div>

            {/* Contact Fields (only show when toggle is on) */}
            {settings.show_contact_info && (
              <>
                <div className="border-t border-gray-200 pt-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Provide custom contact information for this fair. Leave fields empty to use your account defaults.
                  </p>

                  {/* Email Input */}
                  <div>
                    <label htmlFor="public-email" className="block text-sm font-medium text-gray-700">
                      Public Email (Optional)
                    </label>
                    <input
                      type="email"
                      id="public-email"
                      value={settings.public_email || ''}
                      onChange={(e) => setSettings({ ...settings, public_email: e.target.value })}
                      placeholder="your.email@example.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dusty-teal focus:ring-dusty-teal sm:text-sm"
                      data-testid="public-email-input"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Email address for vendors to contact you about this fair.
                    </p>
                  </div>

                  {/* Phone Input */}
                  <div className="mt-4">
                    <label htmlFor="public-phone" className="block text-sm font-medium text-gray-700">
                      Public Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      id="public-phone"
                      value={settings.public_phone || ''}
                      onChange={(e) => setSettings({ ...settings, public_phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dusty-teal focus:ring-dusty-teal sm:text-sm"
                      data-testid="public-phone-input"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Phone number for vendors to contact you about this fair.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Preview Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Preview</h3>
              <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
                <p className="text-sm text-gray-700 font-medium mb-2">How vendors will see your contact:</p>
                {settings.show_contact_info ? (
                  <div className="space-y-1 text-sm text-gray-600">
                    {settings.public_email && (
                      <p>Email: <span className="font-medium">{settings.public_email}</span></p>
                    )}
                    {settings.public_phone && (
                      <p>Phone: <span className="font-medium">{settings.public_phone}</span></p>
                    )}
                    {!settings.public_email && !settings.public_phone && (
                      <p className="text-gray-500 italic">Using account default contact information</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">Contact information is hidden</p>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex items-center justify-end gap-3 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              disabled={!hasChanges || saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dusty-teal disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="cancel-button"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={!hasChanges || saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dusty-teal disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="save-button"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>

        {/* Help Text */}
        <div className="mt-6 bg-moonbone border border-teal-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-ink mb-2">Privacy Tips</h3>
          <ul className="text-sm text-ink space-y-1 list-disc list-inside">
            <li>Settings are per-fair, allowing different contact info for different fairs</li>
            <li>Toggle visibility on/off without losing your custom contact information</li>
            <li>Leave fields empty to use your account's default contact information</li>
            <li>Only vendors viewing this specific fair will see these contact details</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
